import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { fetchAssets, patchAsset, uploadTileImage } from "@/lib/api";
import { PageHeader } from "../_shared";

export default function AssetsTab() {
    const [assets, setAssets] = useState([]);
    const [editingId, setEditingId] = useState(null);

    const refresh = useCallback(
        async () => setAssets(await fetchAssets()),
        [],
    );
    useEffect(() => {
        refresh();
    }, [refresh]);

    return (
        <div data-testid="assets-tab">
            <PageHeader
                title="Asset"
                accent="image bank."
                description="Reusable image slots used across the landing page. Upload or paste a URL to replace."
            />
            <div
                data-testid="assets-grid"
                className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-line border border-line rounded-2xl overflow-hidden"
            >
                {assets.map((a) => (
                    <AssetSlot
                        key={a.id}
                        asset={a}
                        open={editingId === a.id}
                        onToggle={() =>
                            setEditingId(editingId === a.id ? null : a.id)
                        }
                        onSaved={async () => {
                            setEditingId(null);
                            await refresh();
                            toast("Asset updated.");
                        }}
                    />
                ))}
            </div>
        </div>
    );
}

function AssetSlot({ asset, open, onToggle, onSaved }) {
    const [label, setLabel] = useState(asset.label || "");
    const [imageUrl, setImageUrl] = useState(asset.image_url || "");
    const [busy, setBusy] = useState(false);
    const fileRef = useRef(null);

    useEffect(() => {
        setLabel(asset.label || "");
        setImageUrl(asset.image_url || "");
    }, [asset, open]);

    const onUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setBusy(true);
        try {
            const res = await uploadTileImage(file);
            setImageUrl(res.url);
            toast("Image uploaded.");
        } catch (err) {
            toast(err?.response?.data?.detail || "Upload failed.");
        } finally {
            setBusy(false);
        }
    };

    const onSave = async () => {
        const l = label.trim();
        if (!l) {
            toast("Label is required.");
            return;
        }
        setBusy(true);
        try {
            await patchAsset(asset.id, {
                label: l,
                image_url: imageUrl || null,
            });
            await onSaved?.();
        } catch (err) {
            toast(err?.response?.data?.detail || "Save failed.");
        } finally {
            setBusy(false);
        }
    };

    const origin = window.location.origin;
    const thumb = imageUrl
        ? imageUrl.startsWith("/")
            ? `${origin}${imageUrl}`
            : imageUrl
        : null;

    return (
        <div data-testid={`asset-${asset.key}`} className="bg-background">
            <button
                type="button"
                onClick={onToggle}
                className="w-full text-left p-5 flex items-center gap-4 hover:bg-white/[0.04] transition-colors"
            >
                <div className="w-16 h-16 shrink-0 border border-line bg-white/[0.03] overflow-hidden grid place-items-center rounded-xl">
                    {thumb ? (
                        <img
                            src={thumb}
                            alt=""
                            className="w-full h-full object-contain"
                        />
                    ) : (
                        <span className="text-ink-soft text-[0.6rem] font-display tracking-[0.2em]">
                            EMPTY
                        </span>
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-[0.6rem] tracking-[0.25em] uppercase text-ink-soft">
                        {asset.key}
                    </div>
                    <div className="font-display font-bold truncate">
                        {label || "-"}
                    </div>
                </div>
                <div className="text-xs text-ink-soft">
                    {open ? "Close" : "Edit"}
                </div>
            </button>
            {open && (
                <div className="p-5 border-t border-line space-y-4">
                    <div>
                        <label
                            className="block text-[0.65rem] tracking-[0.25em] uppercase text-ink-soft mb-1"
                            htmlFor={`asset-lbl-${asset.id}`}
                        >
                            Label
                        </label>
                        <input
                            id={`asset-lbl-${asset.id}`}
                            data-testid={`asset-label-input-${asset.key}`}
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                            className="w-full bg-transparent border border-line rounded-lg px-3 py-2 outline-none focus:border-white/40"
                        />
                    </div>
                    <div>
                        <label className="block text-[0.65rem] tracking-[0.25em] uppercase text-ink-soft mb-1">
                            Image URL
                        </label>
                        <input
                            data-testid={`asset-url-input-${asset.key}`}
                            value={imageUrl}
                            onChange={(e) => setImageUrl(e.target.value)}
                            placeholder="https:// or /api/files/…"
                            className="w-full bg-transparent border border-line rounded-lg px-3 py-2 outline-none focus:border-white/40"
                        />
                        <input
                            ref={fileRef}
                            type="file"
                            accept="image/*"
                            onChange={onUpload}
                            className="hidden"
                            data-testid={`asset-upload-${asset.key}`}
                        />
                        <button
                            type="button"
                            onClick={() => fileRef.current?.click()}
                            className="vc-btn vc-btn-ghost mt-2 text-xs"
                            disabled={busy}
                            data-testid={`asset-upload-btn-${asset.key}`}
                        >
                            {busy ? "Uploading…" : "Upload image"}
                        </button>
                    </div>
                    <button
                        type="button"
                        onClick={onSave}
                        disabled={busy}
                        className="vc-btn"
                        data-testid={`asset-save-${asset.key}`}
                    >
                        {busy ? "Saving…" : "Save"}
                    </button>
                </div>
            )}
        </div>
    );
}
